// @ts-strict-ignore
import sortBy from 'lodash/sortBy';
import mapValues from 'lodash/mapValues';

import {apiClient} from 'common/util/api-client';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {getProjectId, getWorkspaceId} from 'common/report/util';

import {appApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';
import {purgeFetchCache} from '../utils/fetch-cache';

class DataGroupsClient extends BaseClient<DataGroup[]> {
  addNewDataGroup({projectId, propertyName, displayName, metadata}) {
    return apiClient.post(this.buildEndpoint({projectId}), {
      display_name: displayName,
      property_name: propertyName,
      metadata,
    });
  }

  deleteDataGroup({projectId, dataGroupId}) {
    return apiClient.delete(this.buildEndpoint({projectId, dataGroupId}));
  }

  updateDataGroupName({projectId, dataGroupId, newDataGroupName}) {
    return apiClient.post(this.buildEndpoint({projectId, dataGroupId}), {display_name: newDataGroupName});
  }

  updateDataGroupMetadata({projectId, dataGroupId, newDataGroupMetadata}) {
    let endpoint = this.buildEndpoint({projectId, dataGroupId});
    endpoint = endpoint.concat(`update_metadata`);
    return apiClient.post(endpoint, {
      metadata: newDataGroupMetadata,
    });
  }

  query(params, queryOpts = {}) {
    // Normalize input so the cache works as expected.
    // Note that each instances of DataGroupsClient creates its own cache. It should be a singleton, but currently e.g.
    // insights-app and nested mp-query-builder each instantiate an instance which can produce identical requests.
    const {projectId, workspaceId} = params;
    const normalizedParams = {
      projectId: parseInt(projectId, 10),
      workspaceId: workspaceId ? parseInt(workspaceId, 10) : 0,
    };

    return super.query(normalizedParams, queryOpts);
  }

  buildEndpoint({
    projectId,
    workspaceId,
    dataGroupId,
  }: {
    projectId: number;
    workspaceId?: number;
    dataGroupId?: number;
  }) {
    return appApiUrl(`data-groups/${dataGroupId ? `${dataGroupId}/` : ``}`, {projectId, workspaceId});
  }

  processResponse(response): DataGroup[] {
    if (!response.results) {
      return [];
    }
    const groups = response.results.map((dataGroup) => ({
      displayName: dataGroup.display_name,
      groupID: dataGroup.data_group_id,
      isReady: dataGroup.is_ready,
      propertyName: dataGroup.property_name,
      type: dataGroup.type,
      metadata: dataGroup.metadata,
    }));

    return sortBy(groups, `displayName`);
  }
}

export const dataGroupsClient = new DataGroupsClient();

function buildEndpoint(dataGroupId?: string): string {
  return appApiUrl(`data-groups/${dataGroupId ? `${dataGroupId}/` : ``}`, {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
  });
}

export function dataGroupIdMap(groupsData: TODO[] | null) {
  if (!groupsData) {
    return null;
  }

  const result = groupsData.reduce((acc, dataGroupData) => {
    acc[dataGroupData.data_group_id] = {
      displayName: dataGroupData.display_name,
      groupID: dataGroupData.data_group_id,
      isReady: dataGroupData.is_ready,
      propertyName: dataGroupData.property_name,
      type: dataGroupData.type,
      metadata: dataGroupData.metadata,
    };
    return acc;
  }, {});
  return result;
}

//** This is the new dataGroupsClient. Leaving the existing one to allow for incremental migration. */
export const dataGroupsResourceClient: ApiResourceClient<DataGroup> = {
  index(): {cache: Promise<{[key: string]: DataGroup}>; network: Promise<{[key: string]: DataGroup}>} {
    const fetches = apiClient.hydratedGet<TODO[]>(buildEndpoint());
    return mapValues(fetches, (fetch) => fetch.then((groupsData) => dataGroupIdMap(groupsData)));
  },

  purgeCache(dataGroupId?: string): void {
    purgeFetchCache({everything: true, path: buildEndpoint(dataGroupId)});
  },
};
